import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    showOrderNumber = "showOrderNumber",
    showDate = "showDate",
    showOrderTotal = "showOrderTotal",
    showStatus = "showStatus",
    showShipTo = "showShipTo",
    showPONumber = "showPONumber",
    showReorderProducts = "showReorderProducts",
}

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    webOrderNumber: ["webOrderNumber"],
    orderDate: ["orderDate", "erpOrderNumber"],
    orderTotal: ["orderTotal"],
    status: ["status"],
    stCompanyName: ["stCompanyName"],
    customerPO: ["customerPO"],
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        [fields.showOrderNumber]: boolean;
        [fields.showDate]: boolean;
        [fields.showOrderTotal]: boolean;
        [fields.showStatus]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showPONumber]: boolean;
        [fields.showReorderProducts]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
        isReordering: state.pages.orderHistory.isReordering,
        isUpdatingCart: state.context.isUpdatingCart,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        language: state.context.session.language,
        showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
        isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,

        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    reorder,
    loadOrders,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface SupplyLandRecentOrdersStyles {
    titleGridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    viewAllGridItem?: GridItemProps;
    title?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
    headerContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    exportLink?: LinkPresentationProps;
}

export const supplyLandRecentOrdersStyles: SupplyLandRecentOrdersStyles = {
    container: {
        css: css`
            border: 1px solid #888;
            overflow: auto;
        `,
    },
    titleGridContainer: {
        css: css`
            padding-bottom: 20px;
        `,
        offsetCss: css`
            margin-bottom: 10px;
            margin-top: 20px;
            border-bottom: 1px solid ${getColor("common.border")};
            &&& {
                width: 100%;
            }
        `,
    },
    titleGridItem: {
        width: 8,
        css: css`
            padding: 15px;
        `,
    },
    title: {
        css: css`
            margin-bottom: 1rem;
        `,
    },
    viewAllGridItem: {
        width: 4,
    },
    viewAllLink: {
        css: css`
            margin-left: auto;
            margin-top: 5px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: left;
        `,
    },
    noResultsText: {
        size: 15,
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    reorderButton: {
        variant: "secondary",
        sizeVariant: "small",
    },
    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    checkboxHeader: {
        tight: true,
    },
    headerContainer: {
        gap: 8,
        css: css`
            padding: 20px 0;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            > * {
                padding-right: 10px;
            }
        `,
    },
    exportLink: {
        css: css`
            margin-left: 10px;
        `,
    },
};

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC, webOrderNumber DESC",
};

const styles = supplyLandRecentOrdersStyles;

class SupplyLandRecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    headerClick(sortField: string) {
        const previousSort = this.props.parameter.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        this.props.updateSearchFields({ sort });
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!this.props.parameter.sort) {
            return sorted;
        }
        const [primaryField] = this.props.parameter.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) =>
        this.props.reorder({
            orderNumber,
            onSuccess: () => this.onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder),
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });

    onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!this.props.showAddToCartConfirmationDialog) {
            return;
        }
        this.props.toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history: this.props.history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const isTrueOrUndefined = (value?: boolean) => value === true || value === undefined;

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        const header = (
            <GridContainer {...styles.titleGridContainer}>
                <GridItem width={8}>
                    <Typography {...styles.title} variant="h2">
                        {translate("My Recent Orders")}
                    </Typography>
                </GridItem>
                <GridItem width={4}>
                    <Link
                        {...styles.viewAllLink}
                        href="/MyAccount/Orders"
                        icon={{ iconProps: { src: ChevronRight } }}
                        typographyProps={{ size: 15 }}
                        data-test-selector="cardListHeadingViewAllLink"
                    >
                        {translate("View All")}
                    </Link>
                </GridItem>
            </GridContainer>
        );

        if (ordersDataView.value.length === 0) {
            return (
                <>
                    {header}
                    <StyledWrapper {...styles.noResultsContainer}>
                        <Typography {...styles.noResultsText}>{translate("No Recent Orders Found")}</Typography>
                    </StyledWrapper>
                </>
            );
        }

        const rows = ordersDataView.value.map(order => {
            return {
                id: order.id,
                linkOrderNumber: order.webOrderNumber || order.erpOrderNumber,
                date: order.orderDate
                    ? getLocalizedDateTime({
                          dateTime: new Date(order.orderDate),
                          language: this.props.language,
                      })
                    : "",
                orderNumber: order.webOrderNumber || order.erpOrderNumber,
                shipTo: `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`,
                status: order.statusDisplay,
                po: order.customerPO,
                total: order.orderGrandTotalDisplay,
                isVmiOrder: !!order.vmiLocationId,
            };
        });

        const { showOrderNumber, showDate, showOrderTotal, showStatus, showShipTo, showPONumber, showReorderProducts } =
            this.props.fields;

        return (
            <>
                {header}
                <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            {isTrueOrUndefined(showOrderNumber) && (
                                <DataTableHeader tight {...styles.orderNumberHeader} title={translate("Order Number")}>
                                    {translate("Order #", "webOrderNumber")}
                                </DataTableHeader>
                            )}
                            {isTrueOrUndefined(showDate) && (
                                <DataTableHeader
                                    tight
                                    {...styles.orderDateHeader}
                                    data-test-selector="tableHeader_OrderDate"
                                >
                                    {translate("Date", "orderDate")}
                                </DataTableHeader>
                            )}
                            {isTrueOrUndefined(showOrderTotal) && (
                                <DataTableHeader tight {...styles.orderTotalHeader}>
                                    {translate("Order Total", "orderTotal")}
                                </DataTableHeader>
                            )}
                            {isTrueOrUndefined(showStatus) && (
                                <DataTableHeader tight {...styles.statusHeader}>
                                    {translate("Status", "status")}
                                </DataTableHeader>
                            )}
                            {isTrueOrUndefined(showShipTo) && (
                                <DataTableHeader {...styles.shipToHeader}>
                                    {translate("Ship To", "stCompanyName")}
                                </DataTableHeader>
                            )}
                            {isTrueOrUndefined(showReorderProducts) && !this.props.isVmiOrderHistoryPage && (
                                <DataTableHeader {...styles.reorderHeader} title={translate("Reorder")} />
                            )}
                        </DataTableHead>
                        <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                            {rows.map(
                                ({ id, linkOrderNumber, date, orderNumber, shipTo, status, po, total, isVmiOrder }) => (
                                    <DataTableRow key={id}>
                                        {isTrueOrUndefined(showOrderNumber) && (
                                            <DataTableCell
                                                {...styles.orderNumberCells}
                                                data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                            >
                                                <OrderDetailPageTypeLink
                                                    title={orderNumber}
                                                    orderNumber={linkOrderNumber}
                                                    isVmiOrder={isVmiOrder}
                                                    isVmiOrderDetailsPage={this.props.isVmiOrderHistoryPage}
                                                />
                                            </DataTableCell>
                                        )}
                                        {isTrueOrUndefined(showDate) && (
                                            <DataTableCell
                                                {...styles.orderDateCells}
                                                data-test-selector="orderHistoryTable_tableCell_date"
                                            >
                                                {date}
                                            </DataTableCell>
                                        )}
                                        {isTrueOrUndefined(showOrderTotal) && (
                                            <DataTableCell {...styles.orderTotalCells}>{total}</DataTableCell>
                                        )}
                                        {isTrueOrUndefined(showStatus) && (
                                            <DataTableCell
                                                {...styles.statusCells}
                                                data-test-selector="orderHistoryTable_tableCell_status"
                                            >
                                                {status}
                                            </DataTableCell>
                                        )}
                                        {isTrueOrUndefined(showShipTo) && (
                                            <DataTableCell {...styles.shipToCells}>{shipTo}</DataTableCell>
                                        )}
                                        {isTrueOrUndefined(showReorderProducts) && !this.props.isVmiOrderHistoryPage && (
                                            <DataTableCell {...styles.reorderCells}>
                                                {this.props.isReordering[orderNumber] ? (
                                                    <LoadingSpinner {...styles.reorderButtonSpinner} />
                                                ) : (
                                                    <Button
                                                        disabled={
                                                            Object.keys(this.props.isReordering).length > 0 ||
                                                            this.props.isUpdatingCart
                                                        }
                                                        {...styles.reorderButton}
                                                        onClick={() =>
                                                            this.reorderClick(orderNumber, linkOrderNumber, isVmiOrder)
                                                        }
                                                        data-test-selector="orderHistoryTable_Reorder"
                                                    >
                                                        {translate("Reorder")}
                                                    </Button>
                                                )}
                                            </DataTableCell>
                                        )}
                                    </DataTableRow>
                                ),
                            )}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            </>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(SupplyLandRecentOrders))),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: "",
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
